import { AccordionSection, Button, FAIcon, ModuleWrapper } from '@/components'
import { Module } from '@/modules/companies'
import {
  PERMISSIONS,
  useAdmin,
  useBreadcrumbOverride,
  useModuleResponsible,
  useOnErrorAuto,
  useParam,
  usePermissions,
  useUser,
} from '@/utils'
import { format } from '@/utils/date-fns'
import { useMutation, useSuspenseQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { subSeconds } from 'date-fns'
import type { ResultOf } from 'gql.tada'
import { useOldAbsenceHandler } from '../components/handler.hooks'
import { AbsenceStatusValue } from '../consts'
import { SET_ABSENCE_OWNER_APPROVAL_MUTATION } from '../mutations'
import { ABSENCE_DETAILS_QUERY } from '../queries'
import { AbsenceComments } from './absence-comments'
import { AbsenceFiles } from './absence-files'
import { AbsenceLogs } from './absence-logs'
import type { AbsenceDialogRef } from '../components'
import { AbsenceDialog } from '../components'
import { BrowserView, MobileView } from 'react-device-detect'
import type { Ref } from 'react'
import { useEffect, useImperativeHandle, useRef, useState } from 'react'
import { getAbsenceDisplayEnd, useAbsenceLockedBefore } from '../util'
import { PermissionsOrMeRequired } from '@/containers/permission-containers'
import { cn } from '@/lib/utils'

export type AbsenceDetailsNode = ResultOf<
  typeof ABSENCE_DETAILS_QUERY
>['absence']

interface AbsenceDetailsInnerRef {
  edit(): void
}

interface Props {
  ref: Ref<AbsenceDetailsInnerRef>
  overrideAbsenceId?: string
  onAbsenceLoad(absence: AbsenceDetailsNode): void
}

export function AbsenceDetailsInner({
  ref,
  overrideAbsenceId,
  onAbsenceLoad,
}: Props) {
  const translations = useTranslate({
    details: 'common.details',
    start: 'common.start',
    end: 'common.end',
    employee: 'common.employee',
    reason: 'common.reason',
    comments: 'common.comments',
    approved: 'timesheets.absence-approved',
    pending: 'timesheets.absence-pending',
    rejected: 'timesheets.absence-rejected',
    approve: 'absences.requests.approve',
    reject: 'absences.requests.reject',
    ownerApprovalLabel: 'absence.details.owner-approval-cell',
    registeredByLabel: 'absence.details.registered-by-label',
    registeredForYouTitle: 'absence.details.registered-for-you-title',
    registeredForYouDescription: [
      'absence.details.registered-for-you-description',
      { name: '' },
    ],
  })

  const dialogRef = useRef<AbsenceDialogRef>(null)
  const hasSetParentAbsence = useRef(false)
  const absenceParamId = useParam('absenceId', !!overrideAbsenceId)
  const absenceId = (overrideAbsenceId ?? absenceParamId) as string
  const { editStatus } = useOldAbsenceHandler()
  const admin = useAdmin()
  const { isResponsibleUser } = useModuleResponsible(Module.ABSENCES)
  const { hasPermissions } = usePermissions()
  const canChangeOthersAbsence = hasPermissions(
    PERMISSIONS.timesheets.change.othersabsence,
    false
  )
  const { createLockedBefore } = useAbsenceLockedBefore()
  const canEditAbsenceStatus = admin || isResponsibleUser

  const onErrorAuto = useOnErrorAuto()
  const { data, refetch } = useSuspenseQuery(ABSENCE_DETAILS_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: { id: absenceId },
    onError: onErrorAuto(),
  })

  const absence = data.absence

  const me = useUser()
  const [setOwnerApproval, { loading: managing }] = useMutation(
    SET_ABSENCE_OWNER_APPROVAL_MUTATION,
    {
      awaitRefetchQueries: true,
      refetchQueries: ['PendingAbsenceCount', 'Sidebar'],
    }
  )

  function respondToOwnAbsence(approved: boolean) {
    void setOwnerApproval({
      variables: { id: absence.id, approved },
      onError: onErrorAuto(),
      // Refetch only on success — chaining .then() would also run after a
      // swallowed error. The mutation returns the updated ownerApprovalStatus,
      // so this just refreshes derived detail data.
      onCompleted: () => void refetch(),
    })
  }

  useImperativeHandle(ref, () => ({
    edit() {
      if (!absence?.absenceType || !dialogRef.current) return

      dialogRef.current.open({
        id: absence.id,
        absenceType: absence.absenceType.id,
        user: absence.user.id,
        datetimeStart: new Date(absence.datetimeStart),
        datetimeEnd: getAbsenceDisplayEnd(absence),
        reason: absence.reason || '',
        files: absence.files.map(f => ({
          id: f.id,
          name: f.fileName,
          url: f.fileUrl,
        })),
      })
    },
  }))

  useEffect(() => {
    if (hasSetParentAbsence.current) return

    onAbsenceLoad(absence)
    hasSetParentAbsence.current = true
  }, [absence, onAbsenceLoad])

  useBreadcrumbOverride(absenceId, absence.user.fullName)

  function getTimeString(side: 'start' | 'end') {
    const date = new Date(
      side === 'start' ? absence.datetimeStart : absence.datetimeEnd
    )
    const type = absence.absenceType
    if (!type || type.durationMode === 'HOURLY') {
      return format(date, 'dd.MM.yyyy HH:mm')
    }

    const normalizedDate = side === 'start' ? date : subSeconds(date, 1)
    return format(normalizedDate, 'dd.MM.yyyy')
  }

  function getStatusLabel(value: string) {
    const normalized = value.toUpperCase()
    if (normalized === AbsenceStatusValue.PENDING) return translations.pending
    if (normalized === AbsenceStatusValue.APPROVED) return translations.approved
    if (normalized === AbsenceStatusValue.REJECTED) return translations.rejected
    return value
  }

  function getStatusStyle(value: string) {
    const normalized = value.toUpperCase()
    if (normalized === AbsenceStatusValue.APPROVED)
      return 'border-emerald-300 bg-emerald-100 text-emerald-900'

    if (normalized === AbsenceStatusValue.PENDING)
      return 'border-amber-300 bg-amber-100 text-amber-900'

    return 'border-rose-300 bg-rose-100 text-rose-900'
  }

  const status = absence.requestStatus
  const timeStart = getTimeString('start')
  const timeEnd = getTimeString('end')
  const employee = absence.user.fullName
  const absenceType = absence.absenceType?.name || '-'
  const reason = absence.reason || '-'

  const registeredBy = absence.registeredBy
  const isOwner = me.id === absence.user.id
  const ownerApprovalStatus = absence.ownerApprovalStatus
  const ownerApprovalPending =
    ownerApprovalStatus?.toUpperCase() === AbsenceStatusValue.PENDING
  const showApprovalBanner = isOwner && ownerApprovalPending
  const showOwnerApprovalCell = !!ownerApprovalStatus

  return (
    <div className="space-y-6">
      <AbsenceDialog
        ref={dialogRef}
        createLockedBefore={createLockedBefore}
        canAddForOthers={canChangeOthersAbsence}
        hideTrigger
      />

      {showApprovalBanner && (
        <AccordionSection
          id="absence-approval"
          collapsible={false}
          className="border-blue-200 bg-blue-50 text-blue-900"
          title={translations.registeredForYouTitle}
          description={translations.registeredForYouDescription({
            name: registeredBy?.fullName ?? '',
          })}
          icon={
            <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-blue-500 text-white">
              <FAIcon icon="user-check" />
            </span>
          }
          action={
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                disabled={managing}
                onClick={() => respondToOwnAbsence(false)}
              >
                <FAIcon icon="xmark" />
                {translations.reject}
              </Button>

              <Button
                disabled={managing}
                onClick={() => respondToOwnAbsence(true)}
              >
                <FAIcon icon="check" />
                {translations.approve}
              </Button>
            </div>
          }
        />
      )}

      <section className="overflow-hidden rounded-2xl bg-white shadow-card">
        <div className="flex flex-col gap-4 px-5 py-5 sm:px-6 sm:py-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-1">
            <p className="text-xs font-semibold tracking-[0.22em] text-stone-500 uppercase">
              {translations.details}
            </p>

            <h2 className="text-2xl font-semibold text-stone-900">
              {employee}
            </h2>

            <p className="text-sm text-stone-600">{absenceType}</p>
          </div>

          {data?.absence && canEditAbsenceStatus ? (
            <button
              type="button"
              className={cn(
                getStatusStyle(status),
                'cursor-pointer inline-flex items-center gap-2 self-start rounded-full border px-3.5 py-1.5 text-sm font-semibold transition hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-300'
              )}
              onClick={() => editStatus(data.absence, undefined, refetch)}
            >
              <span className="h-2 w-2 rounded-full bg-current" />
              {getStatusLabel(status)}
            </button>
          ) : (
            <span
              className={cn(
                getStatusStyle(status),
                'inline-flex items-center gap-2 self-start rounded-full border px-3.5 py-1.5 text-sm font-semibold'
              )}
            >
              <span className="h-2 w-2 rounded-full bg-current" />
              {getStatusLabel(status)}
            </span>
          )}
        </div>

        <dl className="grid border-t border-stone-200 sm:grid-cols-2 xl:grid-cols-4">
          <div className="space-y-1 px-5 py-4 sm:px-6">
            <dt className="text-xs font-semibold text-stone-500 uppercase">
              {translations.start}
            </dt>
            <dd className="text-sm font-medium text-stone-900">{timeStart}</dd>
          </div>

          <div className="space-y-1 border-t border-stone-200 px-5 py-4 sm:border-t-0 sm:px-6 xl:border-l">
            <dt className="text-xs font-semibold text-stone-500 uppercase">
              {translations.end}
            </dt>
            <dd className="text-sm font-medium text-stone-900">{timeEnd}</dd>
          </div>

          <div className="space-y-1 border-t border-stone-200 px-5 py-4 sm:px-6 xl:border-l xl:border-t-0">
            <dt className="text-xs font-semibold text-stone-500 uppercase">
              {translations.employee}
            </dt>
            <dd className="text-sm font-medium text-stone-900">{employee}</dd>
          </div>

          <div className="space-y-1 border-t border-stone-200 px-5 py-4 sm:px-6 xl:border-l xl:border-t-0">
            <dt className="text-xs font-semibold text-stone-500 uppercase">
              {translations.comments}
            </dt>
            <dd className="text-sm font-medium text-stone-900">
              {absence.commentCount}
            </dd>
          </div>

          {registeredBy && (
            <div className="space-y-1 border-t border-stone-200 px-5 py-4 sm:px-6 xl:col-span-2">
              <dt className="text-xs font-semibold text-stone-500 uppercase">
                {translations.registeredByLabel}
              </dt>
              <dd className="text-sm font-medium text-stone-900">
                {registeredBy.fullName}
              </dd>
            </div>
          )}

          {showOwnerApprovalCell && ownerApprovalStatus && (
            <div className="space-y-1 border-t border-stone-200 px-5 py-4 sm:px-6 xl:col-span-2 xl:border-l">
              <dt className="text-xs font-semibold text-stone-500 uppercase">
                {translations.ownerApprovalLabel}
              </dt>
              <dd>
                <span
                  className={cn(
                    getStatusStyle(ownerApprovalStatus),
                    'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold'
                  )}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-current" />
                  {getStatusLabel(ownerApprovalStatus)}
                </span>
              </dd>
            </div>
          )}
        </dl>

        <div className="border-t border-stone-200 px-5 py-4 sm:px-6">
          <p className="text-xs font-semibold text-stone-500 uppercase">
            {translations.reason}
          </p>
          <p className="mt-1.5 text-sm text-stone-800 whitespace-pre-wrap">
            {reason}
          </p>
        </div>
      </section>

      <AbsenceFiles absence={absence} />

      <AbsenceComments absence={absence} />

      <AbsenceLogs absence={absence} />
    </div>
  )
}

export function AbsenceDetails() {
  const translations = useTranslate({
    absences: 'common.absences',
    editAbsence: 'absence.details.edit-absence',
  })

  const innerRef = useRef<AbsenceDetailsInnerRef>(null)

  const absenceId = useParam('absenceId')

  const [absence, setAbsence] = useState<AbsenceDetailsNode>()

  function handleEdit() {
    innerRef.current?.edit()
  }

  return (
    <ModuleWrapper
      title={translations.absences}
      breadcrumbLoading={absenceId}
      headerRight={
        <PermissionsOrMeRequired
          user={{ id: absence?.user.id ?? '' }}
          permissions={PERMISSIONS.timesheets.change.othersabsence}
        >
          <MobileView>
            <Button
              size="icon-md"
              aria-label={translations.editAbsence}
              onClick={handleEdit}
            >
              <FAIcon icon="edit" />
            </Button>
          </MobileView>

          <BrowserView>
            <Button size="lg" onClick={handleEdit}>
              <FAIcon icon="edit" />
              {translations.editAbsence}
            </Button>
          </BrowserView>
        </PermissionsOrMeRequired>
      }
    >
      <AbsenceDetailsInner
        ref={innerRef}
        onAbsenceLoad={value => absence?.id !== value.id && setAbsence(value)}
      />
    </ModuleWrapper>
  )
}
